import { useInfiniteQuery } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { AppError } from '../../api/errors.js';
import type {
  EvaluationRun,
  IWorkflowApiService,
  ListEvaluationRunsResponse,
} from '../../services/workflowApi.interface.js';

export interface EvalRunFilters {
  range?: 'all' | '24h' | '7d' | 'custom';
  from?: string;
  to?: string;
  createdBy?: string;
  origin?: string;
  ciSystem?: string;
  project?: string;
  pipeline?: string;
  buildId?: string;
  branch?: string;
  completeWindow?: boolean;
}

export interface UseEvalRunsReturn {
  runs: EvaluationRun[];
  hasNextPage: boolean;
  isLoading: boolean;
  error: AppError | null;
  refresh: () => void;
  loadMore: () => void;
  isLoadingMore: boolean;
}

export function isValidYyyyMmDd(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
  );
}

const escape = (value: string) => value.trim().replace(/'/g, "''");
const utc = (date: Date) => date.toISOString().replace(/\.\d+Z$/, 'Z');

export function useEvalRuns(
  service: Pick<IWorkflowApiService, 'listEvaluationRuns'>,
  workspaceName: string,
  page = 1,
  pageSize = 25,
  statusFilter = 'All statuses',
  search = '',
  dateFilter = '',
  filters: EvalRunFilters = {},
): UseEvalRunsReturn {
  const text = useMemo(
    () => ({
      search,
      project: filters.project,
      pipeline: filters.pipeline,
      buildId: filters.buildId,
      branch: filters.branch,
    }),
    [
      search,
      filters.project,
      filters.pipeline,
      filters.buildId,
      filters.branch,
    ],
  );
  const [debouncedText, setDebouncedText] = useState(text);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedText(text), 300);
    return () => clearTimeout(timer);
  }, [text]);
  const query = useInfiniteQuery<
    ListEvaluationRunsResponse,
    AppError,
    InfiniteData<ListEvaluationRunsResponse>,
    unknown[],
    number
  >({
    queryKey: [
      'evaluationRuns',
      workspaceName,
      page,
      pageSize,
      statusFilter,
      dateFilter,
      filters.range,
      filters.from,
      filters.to,
      filters.createdBy,
      filters.origin,
      filters.ciSystem,
      filters.completeWindow,
      debouncedText,
    ],
    initialPageParam: (page - 1) * pageSize,
    getNextPageParam: (lastPage, _pages, lastSkip) =>
      lastPage.nextLink != null ? lastSkip + lastPage.value.length : undefined,
    enabled: Boolean(workspaceName),
    retry: false,
    queryFn: async ({ pageParam, signal }) => {
      const inputs = debouncedText;
      const parts: string[] = [];
      if (statusFilter !== 'All statuses')
        parts.push(`runState eq '${escape(statusFilter)}'`);
      if (inputs.search.trim()) {
        const value = escape(inputs.search);
        parts.push(
          `(contains(displayName,'${value}') or contains(id,'${value}'))`,
        );
      }
      if (filters.createdBy !== undefined) {
        if (!filters.createdBy.trim()) {
          throw new AppError(
            'Sign in to filter your runs.',
            'InvalidRunFilter',
            400,
          );
        }
        parts.push(
          `systemProperties/createdBy eq '${escape(filters.createdBy)}'`,
        );
      }
      if (filters.origin) parts.push(`runKind eq '${escape(filters.origin)}'`);
      const ci: string[] = [];
      if (filters.ciSystem)
        ci.push(
          `executionContext/ciContext/system eq '${escape(filters.ciSystem)}'`,
        );
      if (inputs.buildId?.trim())
        ci.push(
          `executionContext/ciContext/buildId eq '${escape(inputs.buildId)}'`,
        );
      for (const [field, value] of [
        ['project', inputs.project],
        ['pipelineName', inputs.pipeline],
        ['branch', inputs.branch],
      ]) {
        if (value?.trim())
          ci.push(
            `contains(executionContext/ciContext/${field},'${escape(value)}')`,
          );
      }
      if (ci.length) {
        parts.push(
          `(executionContext ne null and executionContext/ciContext ne null and ${ci.join(' and ')})`,
        );
      }
      let start: Date | undefined;
      let end: Date | undefined;
      if (filters.range === 'custom') {
        const from = filters.from ?? '';
        const to = filters.to ?? '';
        if (!isValidYyyyMmDd(from) || !isValidYyyyMmDd(to) || from > to) {
          throw new AppError(
            'Choose valid UTC dates with the end on or after the start.',
            'InvalidDateRange',
            400,
          );
        }
        start = new Date(`${from}T00:00:00Z`);
        end = new Date(`${to}T00:00:00Z`);
        end.setUTCDate(end.getUTCDate() + 1);
      } else if (filters.range === '24h' || filters.range === '7d') {
        end = new Date(Date.now());
        start = new Date(
          end.getTime() - (filters.range === '24h' ? 1 : 7) * 86400000,
        );
      } else if (isValidYyyyMmDd(dateFilter)) {
        // Trends buckets metric start times; creation can straddle the adjacent UTC day.
        start = new Date(`${dateFilter}T00:00:00Z`);
        start.setUTCDate(start.getUTCDate() - 1);
        end = new Date(`${dateFilter}T00:00:00Z`);
        end.setUTCDate(end.getUTCDate() + 2);
      }
      if (start && end) {
        parts.push(
          `systemProperties/createdDate ge ${utc(start)} and systemProperties/createdDate lt ${utc(end)}`,
        );
      }
      if (filters.completeWindow && (!start || !end))
        throw new AppError(
          'Choose a bounded date range to load complete evaluation history.',
          'InvalidDateRange',
          400,
        );
      const filter = parts.length ? parts.join(' and ') : undefined;
      const value: EvaluationRun[] = [];
      let skip = pageParam;
      let response: ListEvaluationRunsResponse;
      do {
        signal.throwIfAborted();
        response = await service.listEvaluationRuns(workspaceName, {
          top: pageSize,
          skip,
          filter,
        });
        signal.throwIfAborted();
        if (response.nextLink != null && response.value.length === 0)
          throw new AppError(
            'The evaluation API returned an empty page with more results. Refresh to retry.',
            'InvalidRunPage',
          );
        value.push(...response.value);
        skip += response.value.length;
      } while (filters.completeWindow && response.nextLink != null);
      return { value, nextLink: response.nextLink };
    },
  });
  const runs = useMemo(
    () => query.data?.pages.flatMap((response) => response.value) ?? [],
    [query.data],
  );
  return {
    runs,
    hasNextPage: query.hasNextPage,
    isLoading: query.isFetching && !query.isFetchingNextPage,
    isLoadingMore: query.isFetchingNextPage,
    error: query.error,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetching) void query.fetchNextPage();
    },
    refresh: () => {
      void query.refetch();
    },
  };
}
